import type { Grade, OutsideInConsent, OutsideInView, Signal } from '@cyberscore/contracts';
import { Globe } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession, can } from '../../lib/session';
import { useToast } from '../../ui/Toasts';
import { ReadingList, type Reading } from '../connections/Readings';

interface OutsideInBody {
  source: { kind: string; name: string } | null;
  consent: OutsideInConsent | null;
  run: {
    id: string;
    first_view: boolean;
    source_kind: string;
    source_name: string;
    domains: string[];
    created_at: string;
  } | null;
  view: OutsideInView | null;
  signals: Signal[];
  readings: Reading[];
}

const TONE: Record<Grade, string> = { A: 'ok', B: 'ok', C: 'warn', D: 'warn', F: 'bad' };

/**
 * The outside-in view (task 46): what the business's domains show from the internet, graded by factor, and
 * each readable control set against the answer. Staff can accept a reading as a scan answer. It is shown
 * beside the CyberScore, never mixed into it.
 */
export function OutsideIn({ id }: { id: string }) {
  const { tenant } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ['outside-in', tenant?.id, id];
  const q = useQuery({ queryKey: key, queryFn: () => api<OutsideInBody>('GET', `/assessments/${id}/outside-in`), enabled: !!tenant }); // prettier-ignore
  const done = (b: OutsideInBody) => {
    qc.setQueryData(key, b);
    void qc.invalidateQueries({ queryKey: ['assessments', tenant?.id, id] });
  };
  const collect = useMutation({ mutationFn: (domains: string[]) => api<OutsideInBody>('POST', `/assessments/${id}/outside-in/collect`, { domains }), onSuccess: done }); // prettier-ignore
  const accept = useMutation({ mutationFn: (control: string) => api<OutsideInBody>('POST', `/assessments/${id}/outside-in/accept`, { control }), onSuccess: done }); // prettier-ignore
  const staff = can(tenant, 'outside_in.check');
  const [typed, setDomains] = useState<string | null>(null);

  if (q.isPending || q.isError) return null;
  const d = q.data;
  // The domains the business agreed to, unless staff type others (made-up .test ones, outside production).
  const domains = typed ?? d.consent?.domains.join(', ') ?? '';
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await collect.mutateAsync(domains.split(/[\s,]+/).filter(Boolean));
      setDomains(null);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not check them', 'bad');
    }
  };

  return (
    <section className="card pad" aria-labelledby="oi-h">
      <div className="row-between">
        <h2 id="oi-h" className="section-title row gap">
          <Globe size={16} /> Outside-in view
        </h2>
        {d.view?.grade && <span className={`pill ${TONE[d.view.grade]}`}>Grade {d.view.grade} · {d.view.score}</span>}
      </div>
      {!d.source ? (
        <p className="muted small">No outside-in data source is set up yet.</p>
      ) : (
        <>
          {d.run ? (
            <p className="muted small">
              {d.run.first_view && <><span className="pill warn small-pill">First look</span> Before the business answered, from its submission. </>}
              {d.run.domains.join(', ')} · {d.run.source_name} · {new Date(d.run.created_at).toLocaleDateString('en-MY')}. Shown beside the CyberScore, not part of it.
            </p>
          ) : (
            <p className="muted small">What the business’s domains show from the internet. {d.source.kind === 'scan' ? 'Passive checks: email protection records, the website’s certificate and security headers. No scanning.' : 'Exposed services, out-of-date software, certificates, email protection and leaks.'}</p>
          )}
          <p className="small">
            {d.consent ? (
              <>
                <span className="pill ok small-pill">Consent</span> The business agreed to checks of <b>{d.consent.domains.join(', ')}</b> on {new Date(d.consent.created_at).toLocaleDateString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' })}{d.consent.from_form ? ' on its submission form' : d.consent.given_by_name ? ` (${d.consent.given_by_name})` : ''}.
              </>
            ) : (
              <span className="muted">The business hasn’t agreed to checks of its domains yet. The owner can agree on their Connections page; nothing is checked before then.</span>
            )}
          </p>
          {d.view && (
            <ul className="oi-factors">
              {d.view.factors.filter((f) => f.score !== null).map((f) => (
                <li key={f.key}>
                  <span className={`pill ${TONE[f.grade!]}`}>{f.grade}</span> <b>{f.name}</b>
                  {f.issues.map((i) => (
                    <div key={i} className="muted small">{i}</div>
                  ))}
                </li>
              ))}
            </ul>
          )}
          {d.readings.length > 0 && (
            <>
              <div className="eyebrow mt">Controls the outside view can read</div>
              <ReadingList readings={d.readings} onAccept={staff ? (control) => accept.mutate(control, { onError: (e) => toast(e.message, 'bad') }) : undefined} busy={accept.isPending} />
            </>
          )}
          {staff && (
            <form className="row gap mt" onSubmit={submit}>
              <input className="input grow" aria-label="Domains to check" placeholder="example.com.my, shop.example.com.my" value={domains} onChange={(e) => setDomains(e.target.value)} />
              <button type="submit" className="btn" disabled={!domains.trim() || collect.isPending}>
                {collect.isPending ? 'Checking…' : d.run ? 'Check again' : 'Check'}
              </button>
            </form>
          )}
        </>
      )}
    </section>
  ); // prettier-ignore
}
